import { makeAutoObservable } from 'mobx'
import * as translations from '../translations/index'
import * as translationsNew from '../translationsNew/index'
import { Language, TranslationsInterface } from '../types/translations'
import { TranslationsInterface as TranslationsNewInterface } from '../types/translationsNew'
import { RootStore } from './Root.store'
import { devLog } from '@/methods/devLog'
import { capitalize } from '@/methods/string'

const clientLangThemeMap = {
  bmwTheme: 'bmw',
  cmcTheme: 'cmc',
  enpalTheme: 'enpal',
  ikanoTheme: 'ikano',
  miniTheme: 'bmw',
  onventisTheme: 'onv',
  santanderTheme: 'sant',
}

function deepMerge<T extends Record<string, unknown>>(
  base: T,
  override?: Record<string, unknown>
): T {
  if (!override) {
    return base
  }

  const result = { ...base }

  for (const key in override) {
    const baseValue = base[key]
    const overrideValue = override[key]

    if (
      overrideValue !== null &&
      typeof overrideValue === 'object' &&
      !Array.isArray(overrideValue) &&
      baseValue !== null &&
      typeof baseValue === 'object' &&
      !Array.isArray(baseValue)
    ) {
      result[key as keyof T] = deepMerge(
        baseValue as Record<string, unknown>,
        overrideValue as Record<string, unknown>
      ) as T[keyof T]
    } else if (overrideValue !== undefined) {
      result[key as keyof T] = overrideValue as T[keyof T]
    }
  }

  return result
}

function resolveLanguage(language: string, themeId?: string, configurationId?: string): string {
  if (!themeId) return language

  // get client name from themeId
  const client = clientLangThemeMap[themeId as keyof typeof clientLangThemeMap]
  // get custom client language name
  const computedLanguage = `${language}${capitalize(client)}`

  if (configurationId === 'bmw.ch' && language === 'de') {
    return 'deChBmw'
  }
  // verify if the custom client language exists
  else if (client && translations[computedLanguage as keyof typeof translations]) {
    return computedLanguage
  } else {
    return language
  }
}

export class TranslationsStore {
  private rootStore: RootStore
  translations: TranslationsInterface = translations.en
  translationsNew: TranslationsNewInterface = translationsNew.en
  language = 'en'
  paramLanguage = 'en'

  constructor(rootStore: RootStore) {
    makeAutoObservable(this)
    this.rootStore = rootStore
  }

  setTranslations(baseLanguage: Language, configurationId?: string): void {
    this.language = baseLanguage.substring(0, 2)

    const lang = resolveLanguage(
      this.language,
      this.rootStore.InterfaceState.themeId,
      configurationId
    )

    devLog('Resolved translation property: ', lang)

    this.paramLanguage = lang
    document.documentElement.lang = this.language

    this.translations = deepMerge(translations.en, translations[lang as keyof typeof translations])
    this.translationsNew = deepMerge(
      translationsNew.en,
      translationsNew[lang as keyof typeof translationsNew]
    )
  }
}
